import type { ChangeEvent, FormEvent, ReactElement } from 'react';
import { GAMES_HOME_PATH } from '../routes';

/** Longest query the search accepts (the API's limit). */
export const GAMES_SEARCH_MAX_LENGTH = 100;

/**
 * Games search: a real `GET /a/games?q=` form, so it works in a static page
 * with no script. The app passes `value`/`onChange`/`onSubmit` to search as
 * the visitor types instead.
 */
export function SearchForm({
  id = 'fg-search',
  value,
  defaultValue,
  onChange,
  onSubmit,
  controlsId,
}: {
  id?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  /** The results region the field updates, when it updates one live. */
  controlsId?: string;
}): ReactElement {
  const handleSubmit = onSubmit
    ? (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const field = event.currentTarget.elements.namedItem('q') as HTMLInputElement | null;
        onSubmit(field?.value ?? '');
      }
    : undefined;
  const handleChange = onChange
    ? (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)
    : undefined;
  return (
    <form
      role="search"
      action={GAMES_HOME_PATH}
      method="get"
      className="fg-search"
      onSubmit={handleSubmit}
    >
      <label htmlFor={id} className="fg-visually-hidden">
        Search games
      </label>
      <svg className="fg-search__glass" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="8.5" cy="8.5" r="5.75" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="m13 13 4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        id={id}
        className="fg-search__input"
        type="search"
        name="q"
        placeholder="Games, genres, and more"
        autoComplete="off"
        enterKeyHint="search"
        maxLength={GAMES_SEARCH_MAX_LENGTH}
        value={value}
        defaultValue={value === undefined ? defaultValue : undefined}
        onChange={handleChange}
        aria-controls={controlsId}
      />
    </form>
  );
}
